import React from "react";
import { useLocation } from "react-router-dom";
import { useQuery, useQueryClient } from "react-query";

import NotificationDropdown from "../components/NotificationDropdown";
import ProfileDropdown from "../components/ProfileDropdown";
import { all } from "../helpers/api/notifications";
import { getUserFromCookie } from "../helpers/api/apiCore";
import { getMenuItems } from "../helpers/menu";
import { openMobileMenu } from "../helpers/mobileMenu";
import styles from "./MobileTopbar.module.scss";

// Resolve the current section's label from the route, matching the longest
// menu URL that prefixes the current path (Figma: the row below the brand).
const currentSectionLabel = (pathname: string): string => {
  const flat: { label: string; url: string }[] = [];
  (getMenuItems() || []).forEach((item: any) => {
    if (item.url) flat.push({ label: item.label, url: item.url });
    (item.children || []).forEach((child: any) => {
      if (child.url) flat.push({ label: child.label, url: child.url });
    });
  });
  const match = flat
    .filter((it) => pathname === it.url || pathname.startsWith(it.url + "/"))
    .sort((a, b) => b.url.length - a.url.length)[0];
  return match?.label || "Menu";
};

// Profile menu — mirrors the desktop Topbar.
const ProfileMenus = [
  { label: "Profile", icon: "fe-user", redirectTo: "/profile" },
  { label: "Notification Channels", icon: "fe-bell", redirectTo: "/notification-channels" },
  { label: "Log out", icon: "fe-log-out", redirectTo: "/auth/logout" },
];

// Mobile-only top bar (Figma: Supplier V1 Homepage mobile). Two rows: the
// company brand + notification/profile actions on top, and the section/menu
// trigger (current section name + chevron) below it — tapping it opens the
// navigation drawer. Hidden ≥1140px where the desktop left sidebar is shown.
const MobileTopbar: React.FC = () => {
  const { pathname } = useLocation();
  const sectionLabel = currentSectionLabel(pathname);
  const user = getUserFromCookie();
  const company: string = user?.supplier?.name || "Supplier";
  const initials =
    company
      .replace(/[^A-Za-z0-9 ]/g, "")
      .split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((w: string) => w[0])
      .join("")
      .toUpperCase() || "S";

  const queryClient = useQueryClient();
  const { data: notifications = [] } = useQuery(["notifications"], async () => {
    const res: any = await all();
    return res?.data?.data ?? [];
  });
  const { data: socketConnected = true } = useQuery(["socket", "connected"], {
    enabled: false,
    initialData: true,
  } as any);

  return (
    <header className={styles.bar}>
      {/* Row 1 — company brand + actions */}
      <div className={styles.brandRow}>
        <div className={styles.brand}>
          <span className={styles.brandLogo}>{initials}</span>
          <div className={styles.brandText}>
            <span className={styles.brandLabel}>Company</span>
            <span className={styles.brandName}>{company}</span>
          </div>
        </div>

        <div className={styles.actions}>
          {/* Search is decorative for now (no search endpoint yet), matching the
              non-functional sidebar search. */}
          <button type="button" className={styles.searchBtn} aria-label="Search">
            <i className="mdi mdi-magnify" />
          </button>
          <span className={`${styles.action} ${styles.actionCircle}`}>
            <NotificationDropdown
              connected={socketConnected as any}
              notifications={(notifications || []).slice(0, 10)}
              onLocalUpdate={(updater) => {
                queryClient.setQueryData(["notifications"], updater as any);
              }}
            />
          </span>
          <span className={styles.action}>
            <ProfileDropdown
              profilePic={user?.avatar}
              menuItems={ProfileMenus}
              username={user?.name}
              userTitle={"Supplier"}
            />
          </span>
        </div>
      </div>

      {/* Row 2 — section / menu trigger (opens the drawer) */}
      <button
        type="button"
        className={styles.menuRow}
        onClick={openMobileMenu}
        aria-label="Open menu"
      >
        <i className={`mdi mdi-view-grid-outline ${styles.menuIcon}`} />
        <span className={styles.menuLabel}>{sectionLabel}</span>
        <i className={`mdi mdi-chevron-down ${styles.menuChevron}`} />
      </button>
    </header>
  );
};

export default MobileTopbar;
